import { ShieldAlert, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { ErrorCodes, errorCode, errorSentence } from '../../components/ErrorToast';
import { Button, Card, EmptyState } from '../../components/ui';
import { ApiError } from '../../lib/errors';
import { LABEL, T, TITLE } from './texts';

/* Общие части страницы «Данные для ИИ-агентов»: сведение «подпись — значение», код ошибки, ошибки загрузки. */

export const SCREEN = 'AdminAgentData';

/** Сбой связи, сервера или ответ не по договору — в серверный журнал (принцип II); отказ по правам — нет. */
export const shouldReport = (e: unknown) =>
  !(e instanceof ApiError) || e.code === 'NETWORK' || e.code === 'INTERNAL' || e.status >= 500;

export const isForbidden = (e: unknown): e is ApiError => e instanceof ApiError && e.status === 403;

/** Сведение: подпись сверху, значение под ней. */
export function Fact({
  label,
  className = '',
  children,
}: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <dt className="text-[12px] text-ink-3">{label}</dt>
      <dd className="mt-0.5 text-[13.5px] break-words text-ink">{children}</dd>
    </div>
  );
}

/**
 * Код ошибки (requestId) для команды разработки. Если текст ошибки — слова сервера, рядом — код ответа (`errorCode`,
 * итоговое ревью T160).
 */
export function ErrorCode({ error }: { error: unknown }) {
  const requestId = error instanceof ApiError ? error.requestId : null;
  const code = errorCode(error);
  if (!requestId && !code) return null;
  return (
    <p className="text-[12px] text-ink-3">
      {LABEL.errorCode} <ErrorCodes requestId={requestId} code={code} className="text-ink-2" />
    </p>
  );
}

/** Не загрузилось: причина, код ошибки и «Повторить». */
export function LoadError({
  title,
  error,
  busy,
  onRetry,
}: { title: string; error: unknown; busy: boolean; onRetry: () => void }) {
  return (
    <Card>
      <EmptyState
        icon={TriangleAlert}
        title={title}
        description={errorSentence(error)}
        action={
          <div className="flex flex-col items-center gap-3">
            <ErrorCode error={error} />
            <Button variant="primary" disabled={busy} onClick={onRetry}>
              {LABEL.retry}
            </Button>
          </div>
        }
      />
    </Card>
  );
}

/** `403`: разрешение меняет только администратор системы. Повтор ничего не даст — кнопки нет. */
export function Forbidden({ error }: { error: ApiError }) {
  return (
    <Card>
      <EmptyState
        icon={ShieldAlert}
        title={TITLE.forbidden}
        description={T.forbidden}
        action={
          <div className="flex flex-col items-center gap-2">
            <p className="text-[13px] text-ink-2">{T.forbiddenFix}</p>
            <ErrorCode error={error} />
          </div>
        }
      />
    </Card>
  );
}
